import React, { useCallback, useEffect, useState } from "react";

import { reportsService } from "api/services/reports.service";
import ContentHeader from "components/Layout/ContentHeader";
import ReportDetailModal from "components/report/ReportDetailModal";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Pagination from "components/UI/Pagination";
import Table, { Column } from "components/UI/Table";
import Tabs from "components/UI/Tabs";

import "pages/AdminPage.scss";
import { PAGE_SIZE, REASON_LABEL, REPORT_TABS, STATUS_BADGE, STATUS_LABEL, TARGET_TYPE_LABEL, TARGET_TYPE_OPTIONS } from "./report.constants";

import type { Report, ReportListParams, ReportListResponse, ReportReason, ReportStatus, ReportTargetType } from "api/types/report.types";

// ─────────────────────────────────────────────
// 컴포넌트
// ─────────────────────────────────────────────
const ReportList: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"all" | ReportStatus>("all");
  const [items, setItems] = useState<Report[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [searchText, setSearchText] = useState("");
  const [filterTargetType, setFilterTargetType] = useState<ReportTargetType | "">("");

  const [selected, setSelected] = useState<Report | null>(null);

  const [resolveModal, setResolveModal] = useState<{ open: boolean; report: Report | null }>({
    open: false,
    report: null,
  });
  const [resolveLoading, setResolveLoading] = useState(false);

  // ─────────────────────────────────────────
  // 목록 조회
  // ─────────────────────────────────────────
  const fetchList = useCallback(
    async (page: number) => {
      setLoading(true);
      try {
        const params: ReportListParams = { page, limit: PAGE_SIZE };
        if (activeTab !== "all") params.status = activeTab;
        if (filterTargetType) params.target_type = filterTargetType;

        const res = await reportsService.list(params);
        const { data, meta } = res.data as ReportListResponse;
        setItems(data ?? []);
        setTotal(meta?.total ?? 0);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    },
    [activeTab, filterTargetType],
  );

  useEffect(() => {
    setCurrentPage(1);
    fetchList(1);
  }, [activeTab, filterTargetType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchList(page);
  };

  // ─────────────────────────────────────────
  // 검색 필터 (클라이언트)
  // ─────────────────────────────────────────
  const filtered = searchText ? items.filter((r) => r.reporter_id.includes(searchText) || r.target_id.includes(searchText) || (r.reporter?.username ?? "").includes(searchText)) : items;

  // ─────────────────────────────────────────
  // 대상 일괄 처리
  // ─────────────────────────────────────────
  const handleResolveTarget = async () => {
    if (!resolveModal.report) return;
    setResolveLoading(true);
    try {
      await reportsService.resolveTarget({
        target_type: resolveModal.report.target_type,
        target_id: resolveModal.report.target_id,
      });
      setResolveModal({ open: false, report: null });
      fetchList(currentPage);
    } catch {
      // ignore
    } finally {
      setResolveLoading(false);
    }
  };

  // ─────────────────────────────────────────
  // 테이블 컬럼 정의
  // ─────────────────────────────────────────
  const columns: Column<Report>[] = [
    {
      key: "id",
      title: "ID",
      width: "60px",
      render: (v) => <span style={{ color: "#71717A", fontSize: 12 }}>{String(v)}</span>,
    },
    {
      key: "target_type",
      title: "대상 유형",
      width: "90px",
      render: (v) => <Badge variant="info">{TARGET_TYPE_LABEL[v as ReportTargetType]}</Badge>,
    },
    {
      key: "target_id",
      title: "대상 ID",
      width: "120px",
      render: (v) => (
        <span
          style={{
            fontFamily: "monospace",
            fontSize: 12,
            color: "#3F3F46",
            background: "#F4F4F5",
            padding: "2px 6px",
            borderRadius: 4,
          }}
        >
          {String(v)}
        </span>
      ),
    },
    {
      key: "reporter_id",
      title: "신고자",
      width: "130px",
      render: (v, row) => <span style={{ fontSize: 13 }}>{row.reporter?.username ?? String(v)}</span>,
    },
    {
      key: "reason",
      title: "신고 사유",
      width: "130px",
      render: (v) => <span style={{ fontSize: 13 }}>{REASON_LABEL[v as ReportReason]}</span>,
    },
    {
      key: "description",
      title: "상세 내용",
      render: (v) => (
        <span
          style={{
            fontSize: 13,
            color: "#71717A",
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: 200,
          }}
        >
          {v ? String(v) : "-"}
        </span>
      ),
    },
    {
      key: "status",
      title: "상태",
      width: "100px",
      render: (v) => <Badge variant={STATUS_BADGE[v as ReportStatus]}>{STATUS_LABEL[v as ReportStatus]}</Badge>,
    },
    {
      key: "created_at",
      title: "신고일",
      width: "110px",
      render: (v) => <span style={{ fontSize: 12 }}>{String(v).slice(0, 10)}</span>,
    },
  ];

  // ─────────────────────────────────────────
  // 렌더
  // ─────────────────────────────────────────
  return (
    <div className="admin-page">
      <ContentHeader title="신고글 관리" description="커뮤니티 신고를 검토하고 처리합니다." />

      <div className="admin-page__card">
        <Tabs items={REPORT_TABS} activeKey={activeTab} onChange={(k) => setActiveTab(k as "all" | ReportStatus)} />

        <div className="admin-page__filter">
          <Input placeholder="신고자 ID, 대상 ID 검색" style={{ width: 240 }} prefix="🔍" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
          <Select value={filterTargetType} onChange={(e) => setFilterTargetType(e.target.value as ReportTargetType | "")} options={TARGET_TYPE_OPTIONS} style={{ width: 140 }} />
          <span style={{ fontSize: 13, color: "#71717A", marginLeft: "auto" }}>총 {total.toLocaleString()}건</span>
        </div>

        {loading ? <div style={{ padding: 40, textAlign: "center", color: "#71717A" }}>불러오는 중...</div> : <Table columns={columns} data={filtered} rowKey="id" onRowClick={setSelected} />}

        <Pagination current={currentPage} total={total} pageSize={PAGE_SIZE} onChange={handlePageChange} />
      </div>

      {/* ─── 상세 모달 ─── */}
      <ReportDetailModal report={selected} onClose={() => setSelected(null)} onStatusUpdated={() => fetchList(currentPage)} onOpenResolve={(r) => setResolveModal({ open: true, report: r })} />

      {/* ─── 일괄 처리 확인 모달 ─── */}
      <Modal
        open={resolveModal.open}
        title="신고 일괄 처리"
        width="440px"
        onClose={() => setResolveModal({ open: false, report: null })}
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button variant="outline" size="sm" onClick={() => setResolveModal({ open: false, report: null })}>
              취소
            </Button>
            <Button variant="primary" size="sm" disabled={resolveLoading} onClick={handleResolveTarget}>
              {resolveLoading ? "처리 중..." : "전체 처리 완료"}
            </Button>
          </div>
        }
      >
        {resolveModal.report && (
          <p style={{ fontSize: 14, color: "#3F3F46", lineHeight: 1.7 }}>
            <strong>
              {TARGET_TYPE_LABEL[resolveModal.report.target_type]} ID: {resolveModal.report.target_id}
            </strong>
            에 대한 모든 신고를 <strong>처리 완료</strong> 상태로 변경합니다.
            <br />
            <span style={{ color: "#71717A", fontSize: 13 }}>이 작업은 해당 대상의 pending/reviewing 상태 신고를 모두 포함합니다.</span>
          </p>
        )}
      </Modal>
    </div>
  );
};

export default ReportList;
